import {
	MrtInitialCellMapping,
	PropositionDetails,
	DoInitialCellMapping,
	TrcInitialCellMapping,
	Nullable,
	OfferPricingOutput,
} from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { formatPricing, mapObject } from "@dune/utils";
import {
	PropositionProductType,
	ReceiptTypeEnum,
	ReceiptType,
	receiptTypeLabels,
	receiptTypeValues,
} from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { useCreateReceiptMutation } from "../../../../api/queries/receipt.queries";
import {
	calculateTotalPrimeFromReceiptFields,
	stringToNumber,
} from "../../../receipts/modals/UpdateReceiptModal";
import { ControlledPriceInput } from "../../../../components/inputs/ControlledPriceInput";

interface CreateReceiptData {
	reference: string;
	propositionId: number;
	type: ReceiptType;
	productType: PropositionProductType;
	issueDate: string;
	duneManagementFeesHT?: number | null;
	duneManagementFeesTaxes?: number | null;
	duneManagementFeesTTC?: number | null;
	intermediaryManagementFeesHT?: number | null;
	intermediaryManagementFeesTaxes?: number | null;
	intermediaryManagementFeesTTC?: number | null;
	brokerCommission?: number | null;
	brokerRemuneration?: number | null;
	agreedRateHT?: number | null;
	agreedRateTTC?: number | null;
	doDetails: Nullable<Partial<OfferPricingOutput<"DO", "INITIAL">>>;
	mrtDetails: Nullable<Partial<OfferPricingOutput<"MRT", "INITIAL">>>;
	trcDetails: Nullable<Partial<OfferPricingOutput<"TRC", "INITIAL">>>;
}

// Schema unifié pour tous les types de produits
const receiptFormSchema = z.object({
	reference: z.string().min(1, "La référence est obligatoire"),
	type: z.enum(ReceiptTypeEnum, {
		error: "Le type est obligatoire",
	}),
	...mapObject(DoInitialCellMapping.output, () => z.string().optional()),
	...mapObject(MrtInitialCellMapping.output, () => z.string().optional()),
	...mapObject(TrcInitialCellMapping.output, () => z.string().optional()),
});

type ReceiptFormSchema = z.infer<typeof receiptFormSchema>;

const getDefaultValues = (
	productType: PropositionProductType,
): ReceiptFormSchema => ({
	reference: "",
	type: ReceiptTypeEnum.NEW_BUSINESS,
	duneManagementFeesHT: "",
	duneManagementFeesTaxes: "",
	duneManagementFeesTTC: "",
	intermediaryManagementFeesHT: "",
	intermediaryManagementFeesTaxes: "",
	intermediaryManagementFeesTTC: "",
	brokerCommission: "",
	brokerRemuneration: "",
	agreedRateHT: "",
	agreedRateTTC: "",
	// DO fields
	...(productType === "DO" && mapObject(DoInitialCellMapping.output, () => "")),
	// MRT fields
	...(productType === "MRT" &&
		mapObject(MrtInitialCellMapping.output, () => "")),
	// TRC fields
	...(productType === "TRC" &&
		mapObject(TrcInitialCellMapping.output, () => "")),
});

interface CreateReceiptModalProps {
	proposition: PropositionDetails;
}

const CreateReceiptModal = ({ proposition }: CreateReceiptModalProps) => {
	const [open, setOpen] = useState(false);
	const createReceiptMutation = useCreateReceiptMutation();

	const form = useForm<ReceiptFormSchema>({
		resolver: zodResolver(receiptFormSchema),
		defaultValues: getDefaultValues(proposition.productType),
	});

	const watchedValues = form.watch();

	const { totalPrimeHT, totalTaxes, totalPrimeTTC } = useMemo(
		() =>
			calculateTotalPrimeFromReceiptFields(
				proposition.productType,
				watchedValues,
			),
		[watchedValues, proposition.productType],
	);

	const onSubmit = async (values: ReceiptFormSchema) => {
		try {
			const issueDate = new Date().toISOString();
			const agreedRateHT = stringToNumber(values.agreedRateHT);
			const agreedRateTTC = stringToNumber(values.agreedRateTTC);

			const baseReceiptData = {
				reference: values.reference,
				propositionId: proposition.id,
				type: values.type,
				productType: proposition.productType,
				issueDate,
				duneManagementFeesHT: stringToNumber(values.duneManagementFeesHT),
				duneManagementFeesTaxes: stringToNumber(values.duneManagementFeesTaxes),
				duneManagementFeesTTC: stringToNumber(values.duneManagementFeesTTC),
				intermediaryManagementFeesHT: stringToNumber(
					values.intermediaryManagementFeesHT,
				),
				intermediaryManagementFeesTaxes: stringToNumber(
					values.intermediaryManagementFeesTaxes,
				),
				intermediaryManagementFeesTTC: stringToNumber(
					values.intermediaryManagementFeesTTC,
				),
				brokerCommission: stringToNumber(values.brokerCommission),
				brokerRemuneration: stringToNumber(values.brokerRemuneration),
				totalClientPrimeHT: totalPrimeHT,
				totalClientPrimeTaxes: totalTaxes,
				totalClientPrimeTTC: totalPrimeTTC,
				agreedRateHT: agreedRateHT ? agreedRateHT / 100 : null,
				agreedRateTTC: agreedRateTTC ? agreedRateTTC / 100 : null,
				doDetails: {},
				mrtDetails: {},
				trcDetails: {},
			};

			// Add details specific to the type
			let receiptData: CreateReceiptData = {
				...baseReceiptData,
			};

			switch (proposition.productType) {
				case "DO":
					receiptData = {
						...receiptData,
						doDetails: {
							doPrimeHT: stringToNumber(values.doPrimeHT),
							doPrimeTaxes: stringToNumber(values.doPrimeTaxes),
							doPrimeTTC: stringToNumber(values.doPrimeTTC),
							existingDamagesPrimeHT: stringToNumber(
								values.existingDamagesPrimeHT,
							),
							existingDamagesPrimeTaxes: stringToNumber(
								values.existingDamagesPrimeTaxes,
							),
							existingDamagesPrimeTTC: stringToNumber(
								values.existingDamagesPrimeTTC,
							),
							cnrPrimeHT: stringToNumber(values.cnrPrimeHT),
							cnrPrimeTaxes: stringToNumber(values.cnrPrimeTaxes),
							cnrPrimeTTC: stringToNumber(values.cnrPrimeTTC),
							terrorismContributionPrimeHT: stringToNumber(
								values.terrorismContributionPrimeHT,
							),
							terrorismContributionPrimeTaxes: stringToNumber(
								values.terrorismContributionPrimeTaxes,
							),
							terrorismContributionPrimeTTC: stringToNumber(
								values.terrorismContributionPrimeTTC,
							),
						},
					};
					break;

				case "MRT":
					receiptData = {
						...receiptData,
						mrtDetails: {
							trcPrimeHT: stringToNumber(values.trcPrimeHT),
							trcPrimeTaxes: stringToNumber(values.trcPrimeTaxes),
							trcPrimeTTC: stringToNumber(values.trcPrimeTTC),
							trcNaturalDisastersPrimeHT: stringToNumber(
								values.trcNaturalDisastersPrimeHT,
							),
							trcNaturalDisastersPrimeTaxes: stringToNumber(
								values.trcNaturalDisastersPrimeTaxes,
							),
							trcNaturalDisastersPrimeTTC: stringToNumber(
								values.trcNaturalDisastersPrimeTTC,
							),
							trcTerrorismContributionPrimeHT: stringToNumber(
								values.trcTerrorismContributionPrimeHT,
							),
							trcTerrorismContributionPrimeTaxes: stringToNumber(
								values.trcTerrorismContributionPrimeTaxes,
							),
							trcTerrorismContributionPrimeTTC: stringToNumber(
								values.trcTerrorismContributionPrimeTTC,
							),
							rcmoPrimeHT: stringToNumber(values.rcmoPrimeHT),
							rcmoPrimeTaxes: stringToNumber(values.rcmoPrimeTaxes),
							rcmoPrimeTTC: stringToNumber(values.rcmoPrimeTTC),
							doCnrPrimeHT: stringToNumber(values.doCnrPrimeHT),
							doCnrPrimeTaxes: stringToNumber(values.doCnrPrimeTaxes),
							doCnrPrimeTTC: stringToNumber(values.doCnrPrimeTTC),
						},
					};
					break;

				case "TRC":
					receiptData = {
						...receiptData,
						trcDetails: {
							damageCoveragePrimeHT: stringToNumber(
								values.damageCoveragePrimeHT,
							),
							damageCoveragePrimeTaxes: stringToNumber(
								values.damageCoveragePrimeTaxes,
							),
							damageCoveragePrimeTTC: stringToNumber(
								values.damageCoveragePrimeTTC,
							),
							damageCoverageFirePrimeHT: stringToNumber(
								values.damageCoverageFirePrimeHT,
							),
							damageCoverageFirePrimeTaxes: stringToNumber(
								values.damageCoverageFirePrimeTaxes,
							),
							damageCoverageFirePrimeTTC: stringToNumber(
								values.damageCoverageFirePrimeTTC,
							),
							damageCoverageOtherDamagesPrimeHT: stringToNumber(
								values.damageCoverageOtherDamagesPrimeHT,
							),
							damageCoverageOtherDamagesPrimeTaxes: stringToNumber(
								values.damageCoverageOtherDamagesPrimeTaxes,
							),
							damageCoverageOtherDamagesPrimeTTC: stringToNumber(
								values.damageCoverageOtherDamagesPrimeTTC,
							),
							damageCoverageNaturalDisastersPrimeHT: stringToNumber(
								values.damageCoverageNaturalDisastersPrimeHT,
							),
							damageCoverageNaturalDisastersPrimeTaxes: stringToNumber(
								values.damageCoverageNaturalDisastersPrimeTaxes,
							),
							damageCoverageNaturalDisastersPrimeTTC: stringToNumber(
								values.damageCoverageNaturalDisastersPrimeTTC,
							),
							rcmoPrimeHT: stringToNumber(values.rcmoPrimeHT),
							rcmoPrimeTaxes: stringToNumber(values.rcmoPrimeTaxes),
							rcmoPrimeTTC: stringToNumber(values.rcmoPrimeTTC),
							terrorismContributionPrimeHT: stringToNumber(
								values.terrorismContributionPrimeHT,
							),
							terrorismContributionPrimeTaxes: stringToNumber(
								values.terrorismContributionPrimeTaxes,
							),
							terrorismContributionPrimeTTC: stringToNumber(
								values.terrorismContributionPrimeTTC,
							),
						},
					};
					break;
			}

			await createReceiptMutation.mutateAsync(receiptData);

			toast.success("La quittance a été créée avec succès");
			setOpen(false);
			form.reset(getDefaultValues(proposition.productType));
		} catch (error) {
			if (error instanceof Error) {
				if (error.message.includes("existe déjà")) {
					toast.error("Une quittance avec cette référence existe déjà");
				} else if (error.message.includes("introuvable")) {
					toast.error("La proposition spécifiée est introuvable");
				} else {
					toast.error(
						`Erreur lors de la création de la quittance: ${error.message}`,
					);
				}
			} else {
				toast.error("Une erreur inattendue s'est produite");
			}
		}
	};

	const renderProductSpecificFields = () => {
		switch (proposition.productType) {
			case "DO":
				return (
					<>
						{/* Assurance Dommages Ouvrage */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Assurance DO HT"
								name="doPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="doPrimeTaxes"
							/>

							<ControlledPriceInput
								control={form.control}
								label="Assurance DO TTC"
								name="doPrimeTTC"
							/>
						</div>

						{/* Dommages aux Existants */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Dommages aux Existants HT"
								name="existingDamagesPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="existingDamagesPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Dommages aux Existants TTC"
								name="existingDamagesPrimeTTC"
							/>
						</div>

						{/* CNR */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="CNR HT"
								name="cnrPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="cnrPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="CNR TTC"
								name="cnrPrimeTTC"
							/>
						</div>

						{/* Contribution Attentats */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats HT"
								name="terrorismContributionPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="terrorismContributionPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats TTC"
								name="terrorismContributionPrimeTTC"
							/>
						</div>
					</>
				);

			case "MRT":
				return (
					<>
						{/* Tous Risques Chantier */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Tous Risques Chantier HT"
								name="trcPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="trcPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Tous Risques Chantier TTC"
								name="trcPrimeTTC"
							/>
						</div>

						{/* Catastrophes Naturelles */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Catastrophes Naturelles HT"
								name="trcNaturalDisastersPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="trcNaturalDisastersPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Catastrophes Naturelles TTC"
								name="trcNaturalDisastersPrimeTTC"
							/>
						</div>

						{/* Contribution Attentats */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats HT"
								name="trcTerrorismContributionPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="trcTerrorismContributionPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats TTC"
								name="trcTerrorismContributionPrimeTTC"
							/>
						</div>

						{/* Responsabilité Civile du Maître d'Ouvrage */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="RC Maître d'Ouvrage HT"
								name="rcmoPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="rcmoPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="RC Maître d'Ouvrage TTC"
								name="rcmoPrimeTTC"
							/>
						</div>

						{/* Dommages Ouvrages (Incluant la CNR) */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="DO (Incluant CNR) HT"
								name="doPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="doPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="DO (Incluant CNR) TTC"
								name="doPrimeTTC"
							/>
						</div>
					</>
				);

			case "TRC":
				return (
					<>
						{/* Garantie de Dommages */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Garantie de Dommages HT"
								name="damageCoveragePrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="damageCoveragePrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Garantie de Dommages TTC"
								name="damageCoveragePrimeTTC"
							/>
						</div>

						{/* Dont Incendie */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Dont Incendie HT"
								name="damageCoverageFirePrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="damageCoverageFirePrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Dont Incendie TTC"
								name="damageCoverageFirePrimeTTC"
							/>
						</div>

						{/* Dont Autres Dommages */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Dont Autres Dommages HT"
								name="damageCoverageOtherDamagesPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="damageCoverageOtherDamagesPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Dont Autres Dommages TTC"
								name="damageCoverageOtherDamagesPrimeTTC"
							/>
						</div>

						{/* Dont Catastrophes Naturelles */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Dont Catastrophes Naturelles HT"
								name="damageCoverageNaturalDisastersPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="damageCoverageNaturalDisastersPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Dont Catastrophes Naturelles TTC"
								name="damageCoverageNaturalDisastersPrimeTTC"
							/>
						</div>

						{/* Responsabilité Civile */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="RC Maître d'Ouvrage HT"
								name="rcmoPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="rcmoPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="RC Maître d'Ouvrage TTC"
								name="rcmoPrimeTTC"
							/>
						</div>

						{/* Contribution Attentats */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats HT"
								name="terrorismContributionPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="terrorismContributionPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats TTC"
								name="terrorismContributionPrimeTTC"
							/>
						</div>
					</>
				);

			case "GFA":
				return (
					<div className="text-center py-8 text-gray-500">
						<p>
							Les détails pour les produits GFA ne sont pas encore disponibles
							dans ce formulaire.
						</p>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button size="sm" className="flex items-center gap-2">
					<PlusIcon size={16} />
					Ajouter une quittance
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-[1000px] max-h-[90vh] overflow-y-auto">
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
						<DialogHeader>
							<DialogTitle>
								Ajouter une quittance - {proposition.productType}
							</DialogTitle>
						</DialogHeader>

						{/* Champs de base */}
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="reference"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Référence quittance</FormLabel>
										<FormControl>
											<Input placeholder="REF-001" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="type"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Type</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="Sélectionner" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{receiptTypeValues.map((type) => (
													<SelectItem key={type} value={type}>
														{receiptTypeLabels[type]}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Champs spécifiques au produit */}
						<div className="space-y-6">
							<h3 className="text-lg font-semibold">
								Détails {proposition.productType}
							</h3>
							{renderProductSpecificFields()}
						</div>

						{/* Frais de gestion */}
						<div className="space-y-4">
							<h3 className="text-lg font-semibold">Frais de Gestion</h3>

							{/* Frais DUNE */}
							<div className="grid grid-cols-3 gap-4">
								<ControlledPriceInput
									control={form.control}
									label="Frais de Gestion DUNE HT"
									name="duneManagementFeesHT"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Taxes"
									name="duneManagementFeesTaxes"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Frais de Gestion DUNE TTC"
									name="duneManagementFeesTTC"
								/>
							</div>

							{/* Frais apporteur */}
							<div className="grid grid-cols-3 gap-4">
								<ControlledPriceInput
									control={form.control}
									label="Frais apporteur HT"
									name="intermediaryManagementFeesHT"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Taxes"
									name="intermediaryManagementFeesTaxes"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Frais apporteur TTC"
									name="intermediaryManagementFeesTTC"
								/>
							</div>
						</div>

						{/* Commissions */}
						<div className="space-y-4">
							<h3 className="text-lg font-semibold">Commissions</h3>
							<div className="grid grid-cols-2 gap-4">
								<ControlledPriceInput
									control={form.control}
									label="Commission Apporteur"
									name="brokerCommission"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Rémunération Apporteur"
									name="brokerRemuneration"
								/>
							</div>
						</div>

						{/* Totaux */}
						<div className="bg-gray-50 p-4 rounded-lg">
							<h3 className="text-lg font-semibold mb-4">Récapitulatif</h3>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Total HT</TableHead>
										<TableHead>Total Taxes</TableHead>
										<TableHead>Total TTC</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									<TableRow>
										<TableCell className="font-bold">
											{formatPricing(totalPrimeHT)}
										</TableCell>
										<TableCell className="font-bold">
											{formatPricing(totalTaxes)}
										</TableCell>
										<TableCell className="font-bold">
											{formatPricing(totalPrimeTTC)}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setOpen(false)}
							>
								Annuler
							</Button>
							<Button type="submit" disabled={createReceiptMutation.isPending}>
								{createReceiptMutation.isPending
									? "Création..."
									: "Créer la quittance"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default CreateReceiptModal;
